'use client';

import { Heading, HorizontalOverflowCarousel, LayoutContainer } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useMemo } from 'react';

import { getVariationLabel } from '@libs/algolia';
import { createItemsViewEvent, useViewItemListImpressions } from '@libs/analytics';
import { useWhiteSpaceCalculation } from '@libs/contentful';

import {
    getCarouselConfig,
    isQuickAddEligible,
    mapYmalProductsToAnalyticsProducts,
    transformYmalProductForDisplay,
} from '../lib';
import { ProductCardWithVariations } from './ProductCardWithVariations';
import { YmalProductsSectionTracker } from './YmalProductsSectionTracker';
import { YmalProductTracker } from './YmalProductTracker';
import type { YmalProductsCarouselProps } from './ymalProductsCarousel.types';
import './YmalProductsCarousel.scss';

/** Presentational product carousel. Callers own feature flags, data fetching, title, and min count. */
export const YmalProductsCarousel = ({
    products,
    title,
    minProducts,
    frefAttribution,
    showQuickAdd = false,
    viewItemList,
}: Readonly<YmalProductsCarouselProps>) => {
    const whiteSpaceCalculate = useWhiteSpaceCalculation();
    const carouselLength = products.length;

    const impressionItems = useMemo(() => {
        if (!viewItemList) return [];

        return createItemsViewEvent(
            mapYmalProductsToAnalyticsProducts(products),
            viewItemList.itemListName,
            viewItemList.itemListId
        ).ecommerce.items;
    }, [products, viewItemList]);

    const { onVisible } = useViewItemListImpressions({ items: impressionItems });

    if (carouselLength < minProducts) {
        return null;
    }

    const { disableArrows, ...carouselConfig } = getCarouselConfig(carouselLength);

    const carouselOptions = {
        className: cn('product-carousel__carousel', disableArrows && 'product-carousel__carousel--disable-arrows'),
        ...carouselConfig,
    };

    const modifier = (translate: number) => {
        const { gap, perView } = carouselOptions.breakpoints['560'];

        if (whiteSpaceCalculate) {
            return whiteSpaceCalculate(translate, perView, gap, carouselLength);
        }

        return translate;
    };

    const carousel = (
        <HorizontalOverflowCarousel {...carouselOptions} modifier={modifier}>
            {products.map((product, index) => {
                const productData = transformYmalProductForDisplay(product, index, frefAttribution);
                const variations = getVariationLabel(product.variations, product.variationData);
                return (
                    <YmalProductTracker
                        key={product.sku}
                        product={product}
                        position={index + 1}
                        frefAttribution={frefAttribution}
                        onView={viewItemList ? () => onVisible(index) : undefined}
                    >
                        <ProductCardWithVariations
                            {...productData}
                            variations={variations}
                            showQuickAdd={showQuickAdd && isQuickAddEligible(product)}
                        />
                    </YmalProductTracker>
                );
            })}
        </HorizontalOverflowCarousel>
    );

    return (
        <LayoutContainer
            className="you-may-also-like-products-carousel"
            data-testid="you-may-also-like-products-carousel"
        >
            <Heading tagName="h2" size="title">
                {title}
            </Heading>

            {viewItemList ? (
                carousel
            ) : (
                <YmalProductsSectionTracker products={products}>{carousel}</YmalProductsSectionTracker>
            )}
        </LayoutContainer>
    );
};
